import type {
  HoldCleaningCurrentCleaning,
  HoldCleaningProjectionSource,
  HoldCleaningSource,
  HoldCleaningVesselFacts,
} from "../models/hold-cleaning.models";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "react-tooltip";

type Source = HoldCleaningProjectionSource | HoldCleaningSource;

export const HOLD_CLEANING_SOURCE_TOOLTIP_ID = "hold-cleaning-source-tooltip";

const sourceDetails: Record<Source, { label: string; description: string }> = {
  EMAIL_INTELLIGENCE: {
    label: "Email intelligence",
    description: "Extracted from an individual cleaning-related email; verify important details before acting.",
  },
  MANUAL: { label: "OpsLink entry", description: "Entered or corrected by an OpsLink user." },
  FORM: { label: "Hold Cleaning form", description: "Imported from an IMOS/Veslink Hold Cleaning form." },
  SNOWFLAKE: { label: "Snowflake snapshot", description: "Imported from a Snowflake vessel or voyage source." },
  manual: { label: "OpsLink upload", description: "Uploaded by an OpsLink user." },
  form: { label: "Hold Cleaning form", description: "Imported from an IMOS/Veslink Hold Cleaning form." },
  snowflake: { label: "Snowflake snapshot", description: "Imported from a Snowflake vessel or voyage source." },
  shipfix: { label: "Shipfix", description: "Collected from a Shipfix-linked message or attachment." },
  outlook: { label: "Outlook", description: "Collected from an Outlook message or attachment." },
  "email-intelligence": {
    label: "Email intelligence",
    description: "Extracted from an individual cleaning-related email; verify important details before acting.",
  },
  "crew-report": { label: "OpsLink Crew Report", description: "Read from the current OpsLink Crew Report." },
  "image-classification": {
    label: "Image categories (preview)",
    description: "Automatic visual suggestions; original images and hold assignments are unchanged.",
  },
  zeronorth: { label: "Legacy ZeroNorth", description: "Retained from an older voyage-plan source." },
  noaa: { label: "Legacy NOAA", description: "Retained from an older weather source." },
  "legacy-sharepoint": {
    label: "Legacy SharePoint crew",
    description: "Retained from the previous crew-data source.",
  },
};

export const describeHoldCleaningSource = (source: Source): string => {
  const details = sourceDetails[source];
  return `Source: ${details.label}. ${details.description}`;
};

export const describeHoldCleaningStep = (task?: HoldCleaningCurrentCleaning["taskStatuses"][number]): string => {
  if (!task) return "No status reported for this hold and step.";
  const evidence = task.evidence;
  if (!evidence) return task.source ? describeHoldCleaningSource(task.source) : "No source reference available.";
  return [
    "Source: cleaning report attachment.",
    `${evidence.sheet}, ${evidence.cells.join(", ")}.`,
    evidence.reportDate ? `Report date: ${evidence.reportDate}.` : "Report date not established.",
    evidence.conflict ? "Reports disagree; status needs review." : "Read directly from the report table.",
    task.status === "NOT_APPLICABLE" ? "Reported as not applicable; not counted as completed." : "",
    task.status === "NOT_REPORTED" && !evidence.conflict ? "No unambiguous status in this table cell." : "",
  ]
    .filter(Boolean)
    .join(" ");
};

export const HoldCleaningSourceHint = ({ source }: { source?: Source | null }) => {
  if (!source) return null;
  return (
    <button
      type="button"
      aria-label={describeHoldCleaningSource(source)}
      data-tooltip-id={HOLD_CLEANING_SOURCE_TOOLTIP_ID}
      data-tooltip-content={describeHoldCleaningSource(source)}
      className="ml-1 inline-flex cursor-help align-middle text-[#899996] focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1F8475] dark:text-gray-300"
    >
      <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
    </button>
  );
};

export const HoldCleaningSourceTooltip = () => (
  <Tooltip
    id={HOLD_CLEANING_SOURCE_TOOLTIP_ID}
    place="top"
    openEvents={{ mouseenter: true, focus: true }}
    closeEvents={{ mouseleave: true, blur: true }}
    positionStrategy="fixed"
    disableStyleInjection={true}
    style={{
      backgroundColor: "#292F2D",
      borderRadius: "8px",
      color: "#fff",
      fontSize: "12px",
      maxWidth: "320px",
      zIndex: 1000,
    }}
  />
);

export const describeHoldCleaningHoldCount = (evidence: HoldCleaningVesselFacts["holdCountEvidence"]): string => {
  if (!evidence?.source) return "Hold count source unavailable.";
  return `Hold count source: ${evidence.source === "BQ5" ? "Baltic questionnaire" : "IHS vessel particulars"}.`;
};

export const HoldCleaningHoldCountHint = ({ evidence }: { evidence: HoldCleaningVesselFacts["holdCountEvidence"] }) => {
  if (!evidence?.source) return null;
  const description = describeHoldCleaningHoldCount(evidence);
  return (
    <button
      type="button"
      aria-label={description}
      data-tooltip-id={HOLD_CLEANING_SOURCE_TOOLTIP_ID}
      data-tooltip-content={description}
      className="ml-1 inline-flex cursor-help align-middle text-[#899996] focus-visible:outline focus-visible:outline-2 dark:text-gray-300"
    >
      <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
    </button>
  );
};
